import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AdminResetPasswordRequest, ForgotPasswordRequest,Operatore,RegisterModel,ResetPasswordRequest } from '../_models/_Index';
import { PendingOrgZeroAccount } from '../_models/PendingOrgZeroAccount';
import {McpAuthResutl} from "../_models/McpAuthResutl";
import {SecurityAnswersStatus, SetSecurityAnswersRequest} from '../_models/security-answers.model';

const cachePrefix = 'archiconnect-v2-';

// La login esterna (SSO) risponde 200 anche in caso di rifiuto applicativo: nel corpo arriva
// l'utente autenticato oppure un `error` testuale. Non e' un errore HTTP, quindi non passa dal
// ramo di errore di subscribe() — ExternalLoginComponent legge esplicitamente entrambe le forme.
type ExternalLoginResult = Operatore & { error?: string };

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    public currentUser: Observable<Operatore>;
    public tokenKey = '';
    private currentUserSubject = new BehaviorSubject<Operatore>(null);

    // Client HTTP che BYPASSA la catena di interceptor (issue #3301, FR-038): serve unicamente alla
    // registrazione self-service, che non deve mai portare il Bearer di chi sta usando il browser.
    // Costruito da HttpBackend, quindi JwtInterceptor non lo vede.
    private readonly httpNoInterceptors: HttpClient;

    constructor(private http: HttpClient, httpBackend: HttpBackend) {
        this.httpNoInterceptors = new HttpClient(httpBackend);
        this.currentUserSubject.next(JSON.parse(localStorage.getItem(cachePrefix+'currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): Operatore {
        return this.currentUserSubject.value;
    }

    public syncCurrentUser(user: Operatore) {
        if (!user) {
            return;
        }

        const current = this.currentUserSubject.value;
        const nextUser = this.mergeCurrentUser(current, user);

        localStorage.setItem(cachePrefix+'currentUser', JSON.stringify(nextUser));
        this.tokenKey = nextUser.jwtToken ?? this.tokenKey;
        this.currentUserSubject.next(nextUser);
    }

    public isInRole(roleName: string): boolean {
        return !!this.currentUserValue?.roles?.includes(roleName);
    }

    login(email: string, password: string) {
        return this.http.post<Operatore>(`${environment.localApiUrl}auth/Accounts/authenticate`,
            { email, password })
            .pipe(map(user => this.storeTokensAndScheduleRefresh(user)));
    }

    loginBadge(codiceBadge: string) {
        return this.http.post<Operatore>(`${environment.localApiUrl}auth/Accounts/authenticate-badge`,
            { codiceBadge })
            .pipe(map(user => this.storeTokensAndScheduleRefresh(user)));
    }

    getSecurityAnswersStatus() {
        return firstValueFrom(
            this.http.get<SecurityAnswersStatus>(`${environment.localApiUrl}auth/Accounts/security-answers/status`)
        );
    }

    setSecurityAnswers(request: SetSecurityAnswersRequest) {
        return firstValueFrom(
            this.http.post<SecurityAnswersStatus>(`${environment.localApiUrl}auth/Accounts/security-answers`, request)
        );
    }

    // Logica DRY condivisa da login(), loginBadge() ed externalLogin().
    private storeTokensAndScheduleRefresh<T extends Operatore>(user: T): T {
        if (user && user.jwtToken) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem(cachePrefix+'currentUser', JSON.stringify(user));
            this.tokenKey = user.jwtToken;
            this.currentUserSubject.next(user);
        }
        this.stopRefreshTokenTimer();
        this.startRefreshTokenTimer();
        return user;
    }

    decodeJwtToken(jwtToken: string, claimName: string | number): string {
      const decodedJWT = JSON.parse(window.atob(jwtToken.split('.')[1]));
      return decodedJWT[claimName];
    }

    externalLogin(idToken: string, refreshToken: string) {
      return this.http.get<ExternalLoginResult>(`${environment.localApiUrl}auth/Accounts/external-login?id_token=${idToken}&refresh_token=${refreshToken}`)
        .pipe(map(user => this.storeTokensAndScheduleRefresh(user)));
    }

  updateRefreshToken() {
      const token = this.currentUserValue?.refreshToken ?? '';
      if (!token) {
          return Promise.resolve(null);
      }
      return firstValueFrom(
          this.http.post<Operatore>(`${environment.localApiUrl}auth/Accounts/update-refresh-token`, { token })
              .pipe(map(user => {
                  if (user && user.jwtToken) {
                      // store user details and jwt token in local storage to keep user logged in between page refreshes
                      localStorage.setItem(cachePrefix+'currentUser', JSON.stringify(user));
                      this.tokenKey = user.jwtToken;
                      this.currentUserSubject.next(user);
                  }
                  this.stopRefreshTokenTimer();
                  this.startRefreshTokenTimer();
                  return user;
              }))
      );
  }

    // Logout automatico per inattività (issue #3203): revoca server-side del refresh token.
    // Va invocato PRIMA di logout(): l'endpoint è [Authorize] e richiede il JWT ancora in storage.
    // Se il refresh token manca risolve subito (no-op). L'errore (es. token già revocato in un
    // altro tab) è gestito best effort dal chiamante e non deve bloccare il logout locale.
    //
    // `credentials` serve a chi ha già eseguito il logout locale e deve revocare comunque: la
    // sessione scaduta mentre l'app era chiusa viene chiusa in modo sincrono all'avvio, e la
    // revoca parte dopo con le credenziali catturate prima. Il Bearer va allora esplicito, perché
    // JwtInterceptor non ne aggiunge più uno una volta azzerato currentUser.
    revokeToken(credentials?: Pick<Operatore, 'jwtToken' | 'refreshToken'>): Promise<void> {
        const token = (credentials ?? this.currentUserValue)?.refreshToken ?? '';
        if (!token) {
            return Promise.resolve(null);
        }
        const headers = credentials?.jwtToken ? { Authorization: `Bearer ${credentials.jwtToken}` } : undefined;
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/revoke-token`, { token }, { headers })
        );
    }

    logout() {
        // remove user from local storage to log user out
        localStorage.removeItem(cachePrefix+'currentUser');
        this.stopRefreshTokenTimer();
        this.tokenKey = '';
        this.currentUserSubject.next(null);
    }

    // Logout MANUALE ("Esci"): chiude la sessione locale e revoca il refresh token sul server.
    // Prima si faceva solo logout(): il refresh token (7 giorni) e il cookie HttpOnly restavano
    // validi, e `refresh-token` — anonimo, legge solo il cookie — avrebbe riemesso un JWT a chiunque
    // avesse il browser.
    //
    // Il logout locale è sincrono; la revoca segue in background con le credenziali catturate
    // PRIMA (l'endpoint è [Authorize], e dopo logout() JwtInterceptor non aggiunge più il Bearer).
    // Best effort: un errore (JWT scaduto, rete, token già revocato da un'altra tab) non deve mai
    // arrivare a chi ha già premuto "Esci". La Promise risolve a revoca conclusa, riuscita o no,
    // e non rigetta mai: i chiamanti la ignorano, serve a chi vuole attenderla.
    // ⚠️ Non ferma il JWT già emesso (7 giorni, stateless): interrompe solo la catena dei rinnovi.
    // Non usarlo dove la sessione è già invalida (ErrorInterceptor su 401): la revoca fallirebbe.
    logoutAndRevoke(): Promise<void> {
        const user = this.currentUserValue;
        this.logout();
        if (!user?.refreshToken) {
            return Promise.resolve();
        }
        return this.revokeToken({ jwtToken: user.jwtToken, refreshToken: user.refreshToken })
            .catch(() => undefined);
    }

    // Percorso AMMINISTRATIVO di registrazione: passa dalla catena di interceptor, quindi porta il
    // Bearer dell'amministratore. È esattamente quel Bearer a qualificare la registrazione lato
    // server (MustChangePassword + account già verificato, FR-015/FR-032).
    // ⚠️ NON rimuovere l'interceptor da qui: romperebbe operatori-create.
    register(account: RegisterModel) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/register`, account)
        );
    }

    // Percorso PUBBLICO di registrazione (issue #3301, FR-038, research D21): stessa URL, stessa
    // firma, ma emessa SENZA header Authorization.
    // Perché serve: JwtInterceptor allega il Bearer a OGNI richiesta quando esiste una sessione,
    // senza alcun filtro sull'URL. Senza questo metodo, un ADMIN o MANAGER già loggato che apre la
    // rotta pubblica /account/register produrrebbe una registrazione qualificata, SALTANDO la
    // verifica dell'indirizzo email di chi si sta auto-registrando.
    // Il bypass vive qui e non nel componente: chiamata HTTP e costruzione dell'URL restano nel
    // service (ai/context/conventions.md:196, FrontEnd/CLAUDE.md:81,84).
    registerSelfService(account: RegisterModel) {
        return firstValueFrom(
            this.httpNoInterceptors.post<void>(`${environment.localApiUrl}auth/Accounts/register`, account)
        );
    }

    mcpAuth(mcpAuthResult: McpAuthResutl) {
      return firstValueFrom(
        this.http.post<void>(`${environment.localApiUrl}mcp/auth/callback`, mcpAuthResult)
      );
    }

    getPendingOrgZeroAccounts() {
        return firstValueFrom(
            this.http.get<PendingOrgZeroAccount[]>(`${environment.localApiUrl}auth/Accounts/pending-org-zero`)
        );
    }

    setOperatoreOrganizzazione(accountId: number, organizzazioneId: number) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/set-operatore-organizzazione`, {
                accountId,
                organizzazioneId
            })
        );
    }

    forgotPassword(email: ForgotPasswordRequest) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/forgot-password`, email)
        );
    }

    verifyEmail(token: string) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/verify-email`, { token })
        );
    }

    resetPassword(reset: ResetPasswordRequest){
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/reset-password`, reset)
        );
    }

    resetPasswordByAdmin(reset: AdminResetPasswordRequest) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/reset-password-admin`, reset)
        );
    }

    // Blocco/sblocco operatore (issue #3169): ADMIN-only lato server.
    setOperatoreActiveAsync(accountId: number, isActive: boolean) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/set-operatore-active`, {
                accountId,
                isActive
            })
        );
    }

    // Scadenza password per operatore (issue #3301, FR-021): ADMIN o MANAGER lato server.
    setOperatorePasswordPolicyAsync(accountId: number, passwordExpiryDays: number | null, mustChangePassword: boolean) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/set-operatore-password-policy`, {
                accountId,
                passwordExpiryDays,
                mustChangePassword
            })
        );
    }

    // Reinvio dell'email di verifica (issue #3301, FR-040): ADMIN o MANAGER lato server.
    // ⚠️ Nessuna password nel payload: prima della feature il reinvio passava da una finta
    // registrazione con password '1234', che oltre a essere fragile inviava l'email sbagliata.
    resendVerificationEmailAsync(accountId: number) {
        return firstValueFrom(
            this.http.post<void>(`${environment.localApiUrl}auth/Accounts/resend-verification-email`, {
                accountId
            })
        );
    }

    // helper functions
    refreshToken() {
        return firstValueFrom(
            this.http.post<Operatore>(`${environment.localApiUrl}auth/Accounts/refresh-token`, {})
        ).then(user => {
            // publish user to subscribers and start timer to refresh token
            this.currentUserSubject.next(user);
            this.tokenKey = user.jwtToken;
            this.startRefreshTokenTimer();
            return user;
        });
    }

    startRefreshTokenTimer() {
        if (!this.tokenKey) {
            return;
        }
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this.tokenKey.split('.')[1]));

        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (60 * 1000);
        refreshTokenTimeout = setTimeout(() => this.refreshToken(), timeout);
    }

    stopRefreshTokenTimer() {
        clearTimeout(refreshTokenTimeout);
    }

    private mergeCurrentUser(current: Operatore | null, user: Operatore): Operatore {
        const nextUser: Operatore = {
            ...(current ?? {}),
            ...user,
            roles: [...(user.roles ?? current?.roles ?? [])]
        };

        nextUser.codice = this.mergeDefinedString(user.codice, current?.codice);
        nextUser.descrizione = this.mergeDefinedString(user.descrizione, current?.descrizione);
        nextUser.title = this.mergeDefinedString(user.title, current?.title);
        nextUser.firstName = this.mergeDefinedString(user.firstName, current?.firstName);
        nextUser.lastName = this.mergeDefinedString(user.lastName, current?.lastName);
        nextUser.userName = this.mergeDefinedString(user.userName, current?.userName);
        nextUser.email = this.mergeDefinedString(user.email, current?.email);
        nextUser.organizzazioneCodice = this.mergeDefinedString(user.organizzazioneCodice, current?.organizzazioneCodice);
        nextUser.gruppoCodice = this.mergeDefinedString(user.gruppoCodice, current?.gruppoCodice);
        nextUser.jwtToken = this.mergeDefinedString(user.jwtToken, current?.jwtToken);
        nextUser.refreshToken = this.mergeDefinedString(user.refreshToken, current?.refreshToken);

        nextUser.id = user.id ?? current?.id;
        nextUser.organizzazioneId = user.organizzazioneId ?? current?.organizzazioneId;
        nextUser.acceptTerms = user.acceptTerms ?? current?.acceptTerms;
        nextUser.isVerified = user.isVerified ?? current?.isVerified;
        nextUser.created = user.created ?? current?.created;
        nextUser.update = user.update ?? current?.update;
        nextUser.imageUrl = user.imageUrl ?? current?.imageUrl;
        // Logout automatico per inattività (issue #3203): preserva il valore su ri-emissioni parziali.
        nextUser.inactivityTimeoutMinutes = user.inactivityTimeoutMinutes ?? current?.inactivityTimeoutMinutes;

        return nextUser;
    }

    private mergeDefinedString(value?: string | null, fallback?: string | null): string | undefined {
        if (value === null || value === undefined) {
            return fallback ?? undefined;
        }

        return value;
    }
}

let refreshTokenTimeout;
